import React from 'react';
import { Input } from '../../../../ui';
import { CoffeeMachine, CoffeeBean, CoffeeCapsule } from '../../CoffeeCardWeb/types';

// Per-rating brew form state. Mirrors the well-known `home.brew` keys (manual:
// doseGrams/grind/notes; capsule: capsuleBrand/capsuleType/intensity). Stored as
// strings while editing; coerced + pruned by buildHomeBrew on submit.
export interface HomeBrewState {
    doseGrams: string;
    grind: string;
    notes: string;
    capsuleBrand: string;
    capsuleType: string;
    intensity: string;
}

export const EMPTY_HOME_BREW: HomeBrewState = {
    doseGrams: '',
    grind: '',
    notes: '',
    capsuleBrand: '',
    capsuleType: '',
    intensity: '',
};

// Prefill the brew form from a machine's flexible defaultBrew (best-effort: keys are Mixed).
export function brewStateFromMachine(machine: CoffeeMachine | undefined): HomeBrewState {
    const d = (machine?.defaultBrew || {}) as Record<string, unknown>;
    const str = (v: unknown) => (v === undefined || v === null ? '' : String(v));
    return {
        doseGrams: str(d.doseGrams),
        grind: str(d.grind),
        notes: str(d.notes),
        capsuleBrand: str(d.capsuleBrand),
        capsuleType: str(d.capsuleType),
        intensity: str(d.intensity),
    };
}

// Build the flexible `home.brew` object for the rating payload, omitting blanks.
// Returns undefined when no meaningful field was filled (method alone is not worth persisting).
export function buildHomeBrew(
    machine: CoffeeMachine | undefined,
    state: HomeBrewState,
): Record<string, unknown> | undefined {
    if (!machine) return undefined;
    const brew: Record<string, unknown> = {};
    if (machine.type === 'manual') {
        brew.method = 'manual';
        if (state.doseGrams.trim() !== '') {
            const dose = Number(state.doseGrams);
            if (!Number.isNaN(dose)) brew.doseGrams = dose;
        }
        if (state.grind.trim()) brew.grind = state.grind.trim();
        if (state.notes.trim()) brew.notes = state.notes.trim();
    } else if (machine.type === 'capsule') {
        brew.method = 'capsule';
        if (state.capsuleBrand.trim()) brew.capsuleBrand = state.capsuleBrand.trim();
        if (state.capsuleType.trim()) brew.capsuleType = state.capsuleType.trim();
        if (state.intensity.trim() !== '') {
            const intensity = Number(state.intensity);
            brew.intensity = Number.isNaN(intensity) ? state.intensity.trim() : intensity;
        }
    } else if (state.notes.trim()) {
        brew.notes = state.notes.trim();
    }
    const meaningful = Object.keys(brew).filter((k) => k !== 'method');
    return meaningful.length > 0 ? brew : undefined;
}

interface HomeBrewFieldsProps {
    machines: CoffeeMachine[];
    selectedMachineId: string | undefined;
    onSelectMachine: (id: string | undefined) => void;
    brew: HomeBrewState;
    onBrewChange: (key: keyof HomeBrewState, value: string) => void;
    // registered bean/capsule attribution. Which picker shows is conditioned on the
    // selected machine's type (capsule → capsule picker; manual/other → bean picker).
    beans: CoffeeBean[];
    capsules: CoffeeCapsule[];
    selectedBeanId: string | undefined;
    selectedCapsuleId: string | undefined;
    onSelectBean: (id: string | undefined) => void;
    onSelectCapsule: (id: string | undefined) => void;
}

// Verified-owner-only home-rating extension: pick one of your registered machines and
// fill a brew config conditioned on the machine type. Rendered inside the isHomeMade branch.
const HomeBrewFields: React.FC<HomeBrewFieldsProps> = ({
    machines,
    selectedMachineId,
    onSelectMachine,
    brew,
    onBrewChange,
    beans,
    capsules,
    selectedBeanId,
    selectedCapsuleId,
    onSelectBean,
    onSelectCapsule,
}) => {
    const selected = machines.find((m) => m._id === selectedMachineId);

    // capsule machines get a capsule picker; manual/other get a bean picker.
    const showCapsulePicker = !!selected && selected.type === 'capsule';
    const showBeanPicker = !!selected && (selected.type === 'manual' || selected.type === 'other');

    return (
        <div className="space-y-3 p-4 border border-ink/10 bg-muted/20 rounded-xl">
            <header className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-700 lowercase">☕ your machine</h3>
                <a
                    href="/coffee/machines"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-pink underline"
                >
                    + register a machine
                </a>
            </header>

            {machines.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                    No active machines yet — register one to attach it to this rating.
                </p>
            ) : (
                <select
                    className="w-full text-sm border border-ink/15 rounded-lg px-3 py-2 bg-white"
                    value={selectedMachineId || ''}
                    onChange={(e) => onSelectMachine(e.target.value || undefined)}
                >
                    <option value="">No machine</option>
                    {machines.map((m) => (
                        <option key={m._id} value={m._id}>
                            {m.brand} {m.model} ({m.type})
                        </option>
                    ))}
                </select>
            )}

            {showBeanPicker && (
                <label className="flex flex-col gap-1 text-xs text-gray-500">
                    <span className="flex items-center justify-between">
                        <span>beans</span>
                        <a
                            href="/coffee/machines"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-pink underline"
                        >
                            + add
                        </a>
                    </span>
                    <select
                        className="w-full text-sm border border-ink/15 rounded-lg px-3 py-2 bg-white"
                        value={selectedBeanId || ''}
                        onChange={(e) => onSelectBean(e.target.value || undefined)}
                    >
                        <option value="">— none —</option>
                        {beans.map((b) => (
                            <option key={b._id} value={b._id}>
                                {[b.roaster, b.name].filter(Boolean).join(' ')}
                            </option>
                        ))}
                    </select>
                </label>
            )}

            {showCapsulePicker && (
                <label className="flex flex-col gap-1 text-xs text-gray-500">
                    <span className="flex items-center justify-between">
                        <span>capsule</span>
                        <a
                            href="/coffee/machines"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-pink underline"
                        >
                            + add
                        </a>
                    </span>
                    <select
                        className="w-full text-sm border border-ink/15 rounded-lg px-3 py-2 bg-white"
                        value={selectedCapsuleId || ''}
                        onChange={(e) => onSelectCapsule(e.target.value || undefined)}
                    >
                        <option value="">— none —</option>
                        {capsules.map((c) => (
                            <option key={c._id} value={c._id}>
                                {[c.brand, c.name].filter(Boolean).join(' ')}
                            </option>
                        ))}
                    </select>
                </label>
            )}

            {selected && selected.type === 'manual' && (
                <div className="grid grid-cols-2 gap-3">
                    <label className="flex flex-col gap-1 text-xs text-gray-500">
                        <span>dose (g)</span>
                        <Input
                            type="number"
                            value={brew.doseGrams}
                            onChange={(e) => onBrewChange('doseGrams', e.target.value)}
                            placeholder="e.g. 18"
                            min={0}
                            step="0.5"
                        />
                    </label>
                    <label className="flex flex-col gap-1 text-xs text-gray-500">
                        <span>grind</span>
                        <Input
                            type="text"
                            value={brew.grind}
                            onChange={(e) => onBrewChange('grind', e.target.value)}
                            placeholder="e.g. fine, 18 clicks"
                            maxLength={80}
                        />
                    </label>
                    <label className="flex flex-col gap-1 text-xs text-gray-500 col-span-2">
                        <span>notes</span>
                        <Input
                            type="text"
                            value={brew.notes}
                            onChange={(e) => onBrewChange('notes', e.target.value)}
                            placeholder="anything worth remembering about this brew"
                            maxLength={500}
                        />
                    </label>
                </div>
            )}

            {selected && selected.type === 'capsule' && (
                <div className="grid grid-cols-2 gap-3">
                    <label className="flex flex-col gap-1 text-xs text-gray-500">
                        <span>capsule brand</span>
                        <Input
                            type="text"
                            value={brew.capsuleBrand}
                            onChange={(e) => onBrewChange('capsuleBrand', e.target.value)}
                            placeholder="e.g. Nespresso Original"
                            maxLength={80}
                        />
                    </label>
                    <label className="flex flex-col gap-1 text-xs text-gray-500">
                        <span>capsule type</span>
                        <Input
                            type="text"
                            value={brew.capsuleType}
                            onChange={(e) => onBrewChange('capsuleType', e.target.value)}
                            placeholder="e.g. Arpeggio"
                            maxLength={80}
                        />
                    </label>
                    <label className="flex flex-col gap-1 text-xs text-gray-500">
                        <span>intensity</span>
                        <Input
                            type="number"
                            value={brew.intensity}
                            onChange={(e) => onBrewChange('intensity', e.target.value)}
                            placeholder="e.g. 8"
                            min={0}
                            step={1}
                        />
                    </label>
                </div>
            )}

            {selected && selected.type === 'other' && (
                <label className="flex flex-col gap-1 text-xs text-gray-500">
                    <span>notes</span>
                    <Input
                        type="text"
                        value={brew.notes}
                        onChange={(e) => onBrewChange('notes', e.target.value)}
                        placeholder="anything worth remembering about this brew"
                        maxLength={500}
                    />
                </label>
            )}
        </div>
    );
};

export default HomeBrewFields;
